import type { CandidateRecord } from '../types/market.js';

const ACTIVE_STATES = new Set<CandidateRecord['state']>(['detected', 'observing', 'developing', 'confirming', 'strong']);

export function shouldQueueForDeepValidation(candidate: CandidateRecord): boolean {
  if (!candidate.baselineReady) {
    return (
      candidate.sampleCoverageMs >= 75_000 &&
      candidate.candidateScore >= 28 &&
      candidate.movementStrength >= 42 &&
      candidate.dataQuality >= 35
    );
  }
  if (candidate.state === 'expired' || candidate.state === 'invalidated') return false;
  if (candidate.direction !== 'neutral') return candidate.candidateScore >= 38;
  return candidate.candidateScore >= 34 && (candidate.movementStrength >= 34 || candidate.directionalEvidence >= 34);
}

export function predictorBlockers(candidate: CandidateRecord, minimumDataQuality: number): string[] {
  const blockers: string[] = [];
  if (!candidate.deepValidated) blockers.push('deep validation incomplete');
  if (!candidate.baselineReady && candidate.sampleCoverageMs < 75_000) blockers.push('live baseline is too young for early deep validation');
  if (candidate.validationStatus === 'failed') blockers.push('deep history unavailable');
  if (candidate.validationCoverage < 0.45) blockers.push('insufficient timeframe coverage');
  if (candidate.direction === 'neutral') blockers.push('direction remains unclear');
  if (!candidate.freshIssuanceAllowed) blockers.push('same-horizon candle is near transition');
  if (!ACTIVE_STATES.has(candidate.state)) blockers.push(`candidate state is ${candidate.state}`);
  if (candidate.dataQuality < minimumDataQuality) blockers.push('data quality below filter');
  if (candidate.candidateScore < 58) blockers.push('combined evidence below predictor threshold');
  if (candidate.directionalEvidence < 42) blockers.push('directional evidence is weak');
  if (candidate.continuationQuality < 40) blockers.push('continuation quality is weak');
  return blockers;
}

export function isPredictorCandidate(candidate: CandidateRecord, minimumDataQuality: number): boolean {
  return predictorBlockers(candidate, minimumDataQuality).length === 0;
}
